/**
 * useBabyGrowth Hook
 *
 * Provides state management and actions for baby growth tracking.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { useLimits } from '@/hooks/useLimits';
import { isValidUUID } from '@/lib/utils';
import {
  BabyGrowthEntry,
  CreateBabyGrowthEntry,
  UpdateBabyGrowthEntry,
  getBabyGrowthEntries,
  addBabyGrowthEntry,
  updateBabyGrowthEntry,
  deleteBabyGrowthEntry,
  countBabyGrowthEntries,
  createInitialGrowthEntry,
} from '@/services/babyGrowthService';
import {
  Gender,
  MeasurementType,
  calculatePercentile,
  getPercentileLabel,
} from '../data/whoGrowthStandards';

export interface BabyInfo {
  index: number;
  name: string;
  gender: Gender;
  birthDate: string;
}

export interface UseBabyGrowthReturn {
  // Data
  entries: BabyGrowthEntry[];
  latestEntry: BabyGrowthEntry | null;
  entryCount: number;
  babies: BabyInfo[];
  selectedBabyIndex: number;
  birthDate: string | null;

  // State
  isLoading: boolean;
  isCreating: boolean;
  error: string | null;

  // Feature availability
  maxEntries: number;
  isUnlimited: boolean;
  canAddEntry: boolean;

  // Actions
  setSelectedBabyIndex: (index: number) => void;
  addEntry: (entry: Omit<CreateBabyGrowthEntry, 'pregnancy_id' | 'baby_index'>) => Promise<BabyGrowthEntry | null>;
  updateEntry: (id: string, updates: UpdateBabyGrowthEntry) => Promise<BabyGrowthEntry | null>;
  deleteEntry: (id: string) => Promise<boolean>;
  refetch: () => Promise<void>;

  // Percentile helpers
  getPercentile: (value: number, ageInDays: number, type: MeasurementType) => number;
  getPercentileInfo: (percentile: number) => ReturnType<typeof getPercentileLabel>;
  getBabyGender: () => Gender;
}

export function useBabyGrowth(): UseBabyGrowthReturn {
  const { lastCompletedPregnancy } = usePregnancyStore();
  const { getNumberLimit, isUnlimited } = useLimits();

  // Get pregnancy ID
  const rawPregnancyId = lastCompletedPregnancy?.id;
  const pregnancyId = isValidUUID(rawPregnancyId) ? rawPregnancyId : null;
  const birthDate = lastCompletedPregnancy?.actual_birth_date || null;

  // State
  const [entries, setEntries] = useState<BabyGrowthEntry[]>([]);
  const [entryCount, setEntryCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBabyIndex, setSelectedBabyIndex] = useState(0);
  const [userId, setUserId] = useState<string | null>(null);

  // Parse baby info from pregnancy data
  const babies: BabyInfo[] = (() => {
    if (!lastCompletedPregnancy) return [];

    const babyData = lastCompletedPregnancy.baby_data;
    const babyNames = lastCompletedPregnancy.baby_names; // string[]
    const defaultBirthDate = birthDate || '';

    // Check for multiples in baby_data.babies
    if (babyData?.babies && Array.isArray(babyData.babies) && babyData.babies.length > 0) {
      return babyData.babies.map((baby, index) => ({
        index,
        name: baby.name || babyNames?.[index] || `Baby ${index + 1}`,
        gender: (baby.gender === 'male' || baby.gender === 'female' ? baby.gender : 'male') as Gender,
        birthDate: defaultBirthDate,
      }));
    }

    // Single baby - get name from babyNames (string array)
    const firstName = babyNames?.[0] || 'Baby';

    return [{
      index: 0,
      name: firstName,
      gender: 'male' as Gender, // Default, as we don't have gender info for single babies without baby_data.babies
      birthDate: defaultBirthDate,
    }];
  })();

  // Limits
  const maxEntries = getNumberLimit('baby_growth_entries_max');
  const isUnlimitedEntries = isUnlimited('baby_growth_entries_max') || maxEntries === -1;
  const canAddEntry = isUnlimitedEntries || entryCount < maxEntries;

  // Get current user
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id || null);
    });
  }, []);

  // Fetch entries
  const fetchEntries = useCallback(async () => {
    if (!pregnancyId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Create initial entry if needed
      await createInitialGrowthEntry(pregnancyId);

      // Fetch entries
      const { data, error: fetchError } = await getBabyGrowthEntries(pregnancyId, selectedBabyIndex);
      if (fetchError) {
        setError(fetchError);
        setEntries([]);
      } else {
        setEntries(data);
      }

      // Get total count
      const { count } = await countBabyGrowthEntries(pregnancyId, selectedBabyIndex);
      setEntryCount(count);
    } catch (err) {
      console.error('Error in fetchEntries:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setIsLoading(false);
    }
  }, [pregnancyId, selectedBabyIndex]);

  // Initial fetch
  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Realtime subscription
  useEffect(() => {
    if (!pregnancyId) return;

    const channel = supabase
      .channel(`baby-growth:${pregnancyId}:${selectedBabyIndex}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'baby_growth',
        filter: `pregnancy_id=eq.${pregnancyId}`,
      }, () => {
        fetchEntries();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [pregnancyId, selectedBabyIndex, fetchEntries]);

  // Actions
  const addEntry = useCallback(async (
    entry: Omit<CreateBabyGrowthEntry, 'pregnancy_id' | 'baby_index'>
  ): Promise<BabyGrowthEntry | null> => {
    if (!pregnancyId || !userId) return null;
    if (!canAddEntry) {
      setError('Limit erreicht. Upgrade auf Premium für unbegrenzte Messungen.');
      return null;
    }

    setIsCreating(true);
    setError(null);

    try {
      const { data, error: createError } = await addBabyGrowthEntry(userId, {
        ...entry,
        pregnancy_id: pregnancyId,
        baby_index: selectedBabyIndex,
      });

      if (createError) {
        setError(createError);
        return null;
      }

      await fetchEntries();
      return data;
    } finally {
      setIsCreating(false);
    }
  }, [pregnancyId, userId, selectedBabyIndex, canAddEntry, fetchEntries]);

  const updateEntry = useCallback(async (
    id: string,
    updates: UpdateBabyGrowthEntry
  ): Promise<BabyGrowthEntry | null> => {
    setError(null);

    const { data, error: updateError } = await updateBabyGrowthEntry(id, updates);
    if (updateError) {
      setError(updateError);
      return null;
    }

    await fetchEntries();
    return data;
  }, [fetchEntries]);

  const deleteEntry = useCallback(async (id: string): Promise<boolean> => {
    setError(null);

    const { success, error: deleteError } = await deleteBabyGrowthEntry(id);
    if (deleteError) {
      setError(deleteError);
      return false;
    }

    await fetchEntries();
    return success;
  }, [fetchEntries]);

  // Percentile helpers
  const getBabyGender = useCallback((): Gender => {
    const baby = babies[selectedBabyIndex];
    return baby?.gender || 'male';
  }, [babies, selectedBabyIndex]);

  const getPercentile = useCallback((
    value: number,
    ageInDays: number,
    type: MeasurementType
  ): number => {
    return calculatePercentile(value, ageInDays, type, getBabyGender());
  }, [getBabyGender]);

  const getPercentileInfo = useCallback((percentile: number) => {
    return getPercentileLabel(percentile);
  }, []);

  return {
    // Data
    entries,
    latestEntry: entries[0] || null,
    entryCount,
    babies,
    selectedBabyIndex,
    birthDate,

    // State
    isLoading,
    isCreating,
    error,

    // Feature availability
    maxEntries,
    isUnlimited: isUnlimitedEntries,
    canAddEntry,

    // Actions
    setSelectedBabyIndex,
    addEntry,
    updateEntry,
    deleteEntry,
    refetch: fetchEntries,

    // Percentile helpers
    getPercentile,
    getPercentileInfo,
    getBabyGender,
  };
}
